import { PLOT_SIZE, SEASON_1_PALETTE, bufferToRgba, type Palette } from '@coderaw/runtime'
import { el } from '../../lib/dom'

export type PlotViewOptions = {
  readonly palette?: Palette
  /** Integer scale, chosen from available size when omitted. */
  readonly scale?: number
  readonly label?: string
}

export type PlotView = {
  readonly element: HTMLCanvasElement
  readonly render: (buffer: Uint8Array) => void
  readonly fit: (availablePx: number) => void
}

/** Plot view zoom from the spec; bigger just squeezes the code beside it. */
export const MAX_SCALE = 6

/** Picks the largest integer zoom that fits, between 1 and MAX_SCALE. */
export const scaleFor = (availablePx: number): number =>
  Math.min(MAX_SCALE, Math.max(1, Math.floor(availablePx / PLOT_SIZE)))

/** A crisp, DPR-aware canvas for a 64x64 palette buffer. */
export const createPlotView = (options: PlotViewOptions = {}): PlotView => {
  const palette = options.palette ?? SEASON_1_PALETTE
  const canvas = el('canvas', {
    class: 'plot-canvas',
    role: 'img',
    'aria-label': options.label ?? 'plot',
  })
  const stage = document.createElement('canvas')
  stage.width = PLOT_SIZE
  stage.height = PLOT_SIZE
  const stageCtx = stage.getContext('2d')
  const rgba = new Uint8ClampedArray(PLOT_SIZE * PLOT_SIZE * 4)
  let scale = options.scale ?? 6
  let last: Uint8Array | null = null

  const resize = (): void => {
    const dpr = Math.max(1, Math.round(window.devicePixelRatio || 1))
    canvas.width = PLOT_SIZE * scale * dpr
    canvas.height = PLOT_SIZE * scale * dpr
    canvas.style.width = `${PLOT_SIZE * scale}px`
    canvas.style.height = `${PLOT_SIZE * scale}px`
    if (last) render(last)
  }

  const render = (buffer: Uint8Array): void => {
    last = buffer
    const ctx = canvas.getContext('2d')
    if (!ctx || !stageCtx) return
    bufferToRgba(buffer, palette, rgba)
    stageCtx.putImageData(new ImageData(rgba, PLOT_SIZE, PLOT_SIZE), 0, 0)
    ctx.imageSmoothingEnabled = false
    ctx.drawImage(stage, 0, 0, canvas.width, canvas.height)
  }

  const fit = (availablePx: number): void => {
    const next = scaleFor(availablePx)
    if (next === scale) return
    scale = next
    resize()
  }

  resize()
  return { element: canvas, render, fit }
}
